/**
 * cardMachineProducts.js — single source of truth for the /card-machines/*
 * product cluster (V2 Product Page standard).
 *
 * Everything a product page renders lives here: metadata, hero copy, quick
 * answer, at-a-glance grid, comparison row, FAQs, internal links and schema
 * inputs. The visible FAQ content and the FAQPage JSON-LD are generated from
 * the same `faqs` array, so schema/visible parity is guaranteed by design.
 *
 * Image URLs follow ASSET_NAMING_CONVENTIONv1.2 on cdn.candopayments.com.
 */

const CDN = "https://cdn.candopayments.com/products/card-machines";

export const CARD_MACHINE_PRODUCTS = {
  "pax-a920-pro": {
    slug: "pax-a920-pro",
    name: "PAX A920 Pro",
    shortName: "A920 Pro",
    manufacturer: "PAX",
    deviceType: "All-round smart terminal",
    metaTitle: "PAX A920 Pro Card Machine UK | All-Round Smart Terminal",
    metaDescription:
      "The PAX A920 Pro smart card machine for UK businesses. Portable Android payments, app-based business tools and staff controls with CanDo support.",
    ogTitle: "PAX A920 Pro Smart Card Machine",
    ogDescription:
      "A versatile Android smart terminal with app-based business tools — the all-rounder for growing UK businesses.",
    heroImage: `${CDN}/pax-a920-pro/a920-pro-front-view_colour_web_transparent.png`,
    heroImageAlt: "PAX A920 Pro smart card machine",
    ogImage: null,
    badges: ["Android", "App Tools", "All-Rounder"],
    heroSubtitle:
      "The versatile smart terminal that does a bit of everything — portable Android payments, app-based business tools and staff controls in one device.",
    quickAnswer: {
      heading: "What is the PAX A920 Pro used for?",
      answer:
        "The PAX A920 Pro is an all-round Android smart card machine that combines portable payments with app-based business tools, suiting growing UK businesses that want one flexible device for counter and floor.",
      supporting:
        "With WiFi and 4G connectivity, staff logins and an app ecosystem on the terminal itself, the A920 Pro is the do-everything choice — capable at the till, on the shop floor and out at events.",
    },
    atAGlance: [
      { label: "Best for", value: "Growing, multi-tasking businesses" },
      { label: "Device type", value: "All-round smart terminal" },
      { label: "Payment environment", value: "Counter, floor or on the road" },
      { label: "Connectivity", value: "WiFi & 4G" },
      { label: "Mobility", value: "Fully portable" },
      { label: "Smart features", value: "Business apps & staff controls" },
      { label: "Key strength", value: "Versatility" },
      { label: "Support model", value: "UK-based CanDo support" },
      { label: "CanDo fit", value: "One device that does it all" },
    ],
    bestFor: [
      "Growing businesses that need one flexible terminal",
      "Shops that also trade at markets and events",
      "Teams that want staff logins and controls",
      "Businesses using app-based tools at the terminal",
      "Mixed counter and floor service",
      "Anyone consolidating hardware onto a single device",
    ],
    whyChoose: [
      {
        title: "One terminal, every job",
        body: "Counter today, shop floor this afternoon, a weekend event next month — the A920 Pro's portability and connectivity options mean you don't need different hardware for different days.",
      },
      {
        title: "Business tools built in",
        body: "App-based tools run on the terminal itself — staff logins, controls and reporting — so the device works for the business, not just the transaction.",
      },
      {
        title: "Smart without the fuss",
        body: "Android power with a straightforward interface staff learn in minutes. Fast, secure transactions with none of the enterprise complexity.",
      },
    ],
    environment: {
      heading: "Which businesses suit the PAX A920 Pro?",
      paragraphs: [
        "The A920 Pro earns its place in businesses that wear multiple hats: the shop that does markets at the weekend, the café that caters events, the salon that pops up at fairs. One device covers the till, the floor and the road.",
        "It differs from the DX8000's hospitality-first smart POS focus and the T650P's customer-facing polish by being the balanced all-rounder — strong at everything, tied to nothing. If you can't predict exactly where you'll take payments next quarter, this is the safe smart choice.",
      ],
      image: `${CDN}/pax-a920-pro/a920-pro-x3-payments_colour_web_transparent.png`,
      imageAlt: "PAX A920 Pro shown accepting swipe, insert and tap payments",
    },
    gallery: [],
    capabilities: [
      "Contactless, chip & PIN and magstripe",
      "Apple Pay & Google Pay",
      "App-based business tools",
      "Staff logins and controls",
      "Printed and digital receipts",
      "EPOS integration options",
    ],
    technicalHighlights: [
      "Android operating system with app support",
      "5\" HD colour touchscreen",
      "WiFi and 4G connectivity",
      "Integrated receipt printer",
      "All-day battery for portable use",
      "PCI-compliant, end-to-end encrypted payments",
    ],
    isRightFor: {
      yes: [
        "You want one device that handles counter, floor and away-days",
        "Staff logins and app-based tools would help you run the business",
        "You value flexibility over a single specialised role",
      ],
      consider: [
        "You want hospitality-grade smart POS workflows — see the DX8000",
        "Your payments never leave a fixed till — see the Desk 5000",
      ],
    },
    faqs: [
      {
        q: "What businesses is the PAX A920 Pro best for?",
        a: "The A920 Pro is best for growing businesses that need one flexible terminal across different settings — a fixed counter, the shop floor, markets or events. Its app-based tools and staff controls suit teams as well as sole traders.",
      },
      {
        q: "Is the PAX A920 Pro an Android card machine?",
        a: "Yes. The A920 Pro runs Android with a 5-inch HD touchscreen, supporting app-based business tools, staff logins and modern payment types alongside standard card acceptance.",
      },
      {
        q: "Does the A920 Pro take Apple Pay and Google Pay?",
        a: "Yes. The A920 Pro accepts Apple Pay, Google Pay and all major contactless payments, plus chip & PIN and magstripe cards.",
      },
      {
        q: "How does the A920 Pro connect?",
        a: "The A920 Pro connects over WiFi and 4G, so it works at a fixed counter, across your premises, and away from the business — anywhere with signal.",
      },
      {
        q: "How is the A920 Pro different from the Ingenico DX8000?",
        a: "Both are Android smart terminals. The DX8000 leans into premium hospitality smart POS workflows, while the A920 Pro is the balanced all-rounder — portable, app-capable and equally at home in retail, services or events.",
      },
      {
        q: "Can CanDo set up the PAX A920 Pro quickly?",
        a: "Yes. Order before 2pm and your terminal can be live the next working day, with setup, switching and UK-based support handled by CanDo.",
      },
    ],
    relatedProducts: ["ingenico-dx8000", "verifone-t650p"],
    relatedServices: [
      { label: "EPOS Systems", href: "/epos-systems" },
      { label: "Switch & Save", href: "/switch-and-save" },
    ],
    relatedIndustries: [
      { label: "Retail", href: "/industries/retail" },
      { label: "Hospitality", href: "/industries/hospitality" },
    ],
    schemaType: "Product",
  },

  "ingenico-dx8000": {
    slug: "ingenico-dx8000",
    name: "Ingenico DX8000",
    shortName: "DX8000",
    manufacturer: "Ingenico",
    deviceType: "Smart Android terminal",
    metaTitle: "Ingenico DX8000 Card Machine UK | Smart Android Terminal",
    metaDescription:
      "Explore the Ingenico DX8000 smart Android card machine for UK businesses. Premium touchscreen payments, app-ready features and CanDo support.",
    ogTitle: "Ingenico DX8000 Smart Android Card Machine",
    ogDescription:
      "A premium Android payment terminal for businesses wanting smart POS capability and modern card payments.",
    heroImage: `${CDN}/ingenico/dx-8000-front-view_colour_web_solid.png`,
    heroImageAlt: "Ingenico DX8000 smart Android card machine",
    heroFramed: true,
    ogImage: `${CDN}/ingenico/dx-8000-front-view_colour_web_solid.png`,
    badges: ["Android", "Touchscreen", "App-Ready"],
    heroSubtitle:
      "A premium Android smart terminal with a full touchscreen experience — built for hospitality, modern retail and businesses that want more from their card machine.",
    quickAnswer: {
      heading: "What is the Ingenico DX8000?",
      answer:
        "The Ingenico DX8000 is a premium Android smart card machine designed for hospitality, retail and businesses that want advanced touchscreen payment technology with flexible payment capabilities.",
      supporting:
        "Running on Android, the DX8000 combines a large HD touchscreen with app-capable smart POS workflows, so it behaves less like a traditional terminal and more like a payment device that grows with your operation.",
    },
    atAGlance: [
      { label: "Best for", value: "Hospitality & advanced retail" },
      { label: "Device type", value: "Smart Android terminal" },
      { label: "Payment environment", value: "Counter, table or floor" },
      { label: "Connectivity", value: "WiFi, 4G & Bluetooth" },
      { label: "Mobility", value: "Fully portable" },
      { label: "Smart features", value: "Android apps & smart POS workflows" },
      { label: "Key strength", value: "Premium touchscreen experience" },
      { label: "Support model", value: "UK-based CanDo support" },
      { label: "CanDo fit", value: "Businesses upgrading older terminals" },
    ],
    bestFor: [
      "Hospitality venues running table service and busy shifts",
      "Modern retail that wants a premium checkout experience",
      "Multi-site operations standardising on one smart device",
      "Businesses that want app-based POS workflows on the terminal",
      "High-volume customer environments",
      "Anyone upgrading from an older push-button terminal",
    ],
    whyChoose: [
      {
        title: "A genuinely smart terminal",
        body: "The DX8000 runs Android, so it supports app-based workflows — not just payment acceptance. Tipping, PIN-on-glass and smart receipt options all live on one device.",
      },
      {
        title: "Premium touchscreen experience",
        body: "A large HD touchscreen replaces fiddly buttons. Staff pick it up quickly and customers get a modern, self-explanatory payment moment.",
      },
      {
        title: "Operational sophistication",
        body: "Designed for environments where payments are part of a bigger workflow — table service, multi-user shifts, and EPOS-connected operations.",
      },
    ],
    environment: {
      heading: "Where does the DX8000 fit best?",
      paragraphs: [
        "The DX8000 suits businesses where the payment moment is part of the service, not an afterthought. In hospitality it moves between the counter, the table and the terrace without losing connection. In retail it gives the checkout a premium, modern feel.",
        "Because it is Android-based, the DX8000 can sit at the heart of smarter operations: staff logins, app-driven features and consistent workflows across multiple sites. If you are running more than one location, standardising on the DX8000 keeps training and support simple.",
      ],
      image: `${CDN}/ingenico/dx-8000-side-angle-hero-right_colour_web_solid.png`,
      imageAlt: "Ingenico DX8000 side angle showing touchscreen payment display",
    },
    gallery: [
      { src: `${CDN}/ingenico/dx-8000-close-up-buttons_colour_web_solid.png`, alt: "Close up of the Ingenico DX8000 touchscreen and controls" },
      { src: `${CDN}/ingenico/dx-8000-close-up-contactless-left_colour_web_solid.png`, alt: "Close up of the Ingenico DX8000 contactless reader" },
      { src: `${CDN}/ingenico/dx-8000-top-down-contents_colour_web_solid.png`, alt: "Ingenico DX8000 box contents from above" },
    ],
    capabilities: [
      "Contactless, chip & PIN and magstripe",
      "Apple Pay & Google Pay",
      "Tipping and gratuity prompts",
      "PIN-on-glass entry",
      "Digital and printed receipts",
      "EPOS integration options",
    ],
    technicalHighlights: [
      "Android operating system with app capability",
      "Large HD colour touchscreen",
      "WiFi, 4G and Bluetooth connectivity",
      "All-day battery for floor and table service",
      "Integrated receipt printer",
      "PCI-compliant, end-to-end encrypted payments",
    ],
    isRightFor: {
      yes: [
        "You want a smart, app-capable terminal rather than a basic card machine",
        "Your team serves customers away from a fixed till",
        "You value a premium, modern customer experience at checkout",
      ],
      consider: [
        "You only need occasional, low-volume payments — see Elavon Mobile",
        "You want a proven portable workhorse without smart features — see the Move 5000",
      ],
    },
    faqs: [
      {
        q: "What businesses is the Ingenico DX8000 best for?",
        a: "The DX8000 is best for hospitality venues, modern retail and multi-site businesses that want a premium Android terminal with smart POS workflows. It suits high-volume environments where staff move between counter, table and floor.",
      },
      {
        q: "Is the Ingenico DX8000 an Android card machine?",
        a: "Yes. The DX8000 runs on Android, which means it supports app-based features, smart receipt options and modern touchscreen workflows alongside standard card payments.",
      },
      {
        q: "Does the DX8000 take Apple Pay and Google Pay?",
        a: "Yes. The DX8000 accepts Apple Pay, Google Pay and all major contactless payments, along with chip & PIN and magstripe cards.",
      },
      {
        q: "Is the DX8000 portable or countertop?",
        a: "The DX8000 is fully portable. It connects over WiFi, 4G and Bluetooth, so it works at a fixed counter but is equally at home moving between tables or serving customers anywhere in your venue.",
      },
      {
        q: "Does the Ingenico DX8000 work with EPOS systems?",
        a: "Yes. The DX8000 offers EPOS integration options, and CanDo can advise on pairing it with an EPOS setup that fits your business.",
      },
      {
        q: "Can CanDo help me switch to the DX8000 from another provider?",
        a: "Yes. CanDo handles switching end to end — we review your current statement, match or beat your rates where we can, and manage setup so you can start taking payments on the DX8000 quickly.",
      },
    ],
    relatedProducts: ["verifone-t650p", "ingenico-move-5000"],
    relatedServices: [
      { label: "EPOS Systems", href: "/epos-systems" },
      { label: "Switch & Save", href: "/switch-and-save" },
    ],
    relatedIndustries: [
      { label: "Hospitality", href: "/industries/hospitality" },
      { label: "Retail", href: "/industries/retail" },
    ],
    schemaType: "Product",
  },

  "ingenico-move-5000": {
    slug: "ingenico-move-5000",
    name: "Ingenico Move 5000",
    shortName: "Move 5000",
    manufacturer: "Ingenico",
    deviceType: "Portable terminal",
    metaTitle: "Ingenico Move 5000 Card Machine UK | Portable Payments",
    metaDescription:
      "Discover the Ingenico Move 5000 portable card machine for hospitality, retail and mobile businesses needing reliable wireless payments.",
    ogTitle: "Ingenico Move 5000 Portable Card Machine",
    ogDescription:
      "A dependable portable card machine trusted by restaurants, cafés and mobile businesses across the UK.",
    heroImage: `${CDN}/ingenico/move-5000-front-view-screen_colour_web_solid.png`,
    heroImageAlt: "Ingenico Move 5000 portable card machine",
    heroFramed: true,
    ogImage: `${CDN}/ingenico/move-5000-front-view-screen_colour_web_solid.png`,
    badges: ["Portable", "Hospitality Favourite", "Proven"],
    heroSubtitle:
      "The proven portable card machine for table service, queue busting and payments on the move — dependable connectivity wherever your customers are.",
    quickAnswer: {
      heading: "What is the Ingenico Move 5000?",
      answer:
        "The Ingenico Move 5000 is a portable card machine built for restaurants, cafés and businesses that need reliable wireless card payments away from a fixed counter.",
      supporting:
        "It is one of the most widely deployed portable terminals in UK hospitality for a reason: long battery life, dependable connections and a design that keeps working through busy shifts.",
    },
    atAGlance: [
      { label: "Best for", value: "Restaurants, cafés & bars" },
      { label: "Device type", value: "Portable terminal" },
      { label: "Payment environment", value: "Table service & on the move" },
      { label: "Connectivity", value: "WiFi, Bluetooth & 4G" },
      { label: "Mobility", value: "Fully portable" },
      { label: "Smart features", value: "Colour touchscreen" },
      { label: "Key strength", value: "Operational reliability" },
      { label: "Support model", value: "UK-based CanDo support" },
      { label: "CanDo fit", value: "Hospitality & mobile teams" },
    ],
    bestFor: [
      "Restaurants taking payment at the table",
      "Cafés and bars with fast-moving service",
      "Queue busting in busy retail",
      "Mobile teams and field services",
      "Events, markets and pop-up trading",
      "Businesses that need a dependable workhorse terminal",
    ],
    whyChoose: [
      {
        title: "Built for real shifts",
        body: "Long battery life and a rugged build mean the Move 5000 keeps taking payments from first covers to last orders, without hunting for a charger.",
      },
      {
        title: "Connections you can rely on",
        body: "WiFi, Bluetooth and 4G with automatic fallback — so a dropped router or busy network doesn't stop you taking payments.",
      },
      {
        title: "Simple for every team member",
        body: "A clear colour touchscreen and familiar layout mean new staff take payments confidently on day one.",
      },
    ],
    environment: {
      heading: "Which businesses suit the Move 5000?",
      paragraphs: [
        "The Move 5000 was designed around hospitality. Taking payment at the table speeds up service, reduces walkouts and keeps card details in the customer's sight — and the Move 5000 does it shift after shift.",
        "It is equally suited to queue busting in retail, mobile trades and event businesses. If your payments happen away from a fixed till and you value reliability over smart extras, this is the terminal to beat.",
      ],
      image: `${CDN}/ingenico/move-5000-side-angle-hero-left_colour_web_solid.png`,
      imageAlt: "Ingenico Move 5000 portable card machine side angle",
    },
    gallery: [
      { src: `${CDN}/ingenico/move-5000-close-up-buttons_colour_web_solid.png`, alt: "Close up of the Ingenico Move 5000 keypad" },
      { src: `${CDN}/ingenico/move-5000-close-up-contactless-right_colour_web_solid.png`, alt: "Close up of the Ingenico Move 5000 contactless reader" },
      { src: `${CDN}/ingenico/move-5000-top-down-contents_colour_web_solid.png`, alt: "Ingenico Move 5000 box contents from above" },
    ],
    capabilities: [
      "Contactless, chip & PIN and magstripe",
      "Apple Pay & Google Pay",
      "Pay-at-table workflows",
      "Tipping and gratuity prompts",
      "Printed and digital receipts",
      "EPOS integration options",
    ],
    technicalHighlights: [
      "3.5\" colour touchscreen with physical keypad",
      "WiFi, Bluetooth and 4G connectivity with fallback",
      "All-day battery designed for full shifts",
      "Fast thermal receipt printer",
      "Drop-tested, spill-resistant build",
      "PCI-compliant, end-to-end encrypted payments",
    ],
    isRightFor: {
      yes: [
        "You take payments at the table or away from a fixed counter",
        "You want a proven, dependable terminal that staff already recognise",
        "Reliability through busy shifts matters more than smart features",
      ],
      consider: [
        "You want Android apps and smart POS workflows — see the DX8000",
        "Your payments happen at a fixed checkout — see the Desk 5000",
      ],
    },
    faqs: [
      {
        q: "Which businesses suit the Ingenico Move 5000?",
        a: "The Move 5000 suits restaurants, cafés, bars and any business taking payments away from a fixed counter — including queue busting in retail, mobile trades and events. It is one of the most widely used portable terminals in UK hospitality.",
      },
      {
        q: "How does the Move 5000 connect?",
        a: "The Move 5000 connects over WiFi, Bluetooth and 4G, with automatic fallback between them. If one connection drops during service, it keeps taking payments on another.",
      },
      {
        q: "Does the Move 5000 support pay-at-table and tipping?",
        a: "Yes. The Move 5000 supports pay-at-table workflows with tipping and gratuity prompts, which is why it is a long-standing favourite in table-service hospitality.",
      },
      {
        q: "Can the Move 5000 take Apple Pay and Google Pay?",
        a: "Yes. It accepts Apple Pay, Google Pay and all major contactless payments alongside chip & PIN and magstripe.",
      },
      {
        q: "How long does the Move 5000 battery last?",
        a: "The Move 5000 is designed to last a full trading day on one charge in typical use, so it comfortably covers a busy service without returning to the dock.",
      },
      {
        q: "How quickly can CanDo set me up with a Move 5000?",
        a: "Order before 2pm and your terminal can be live the next working day. CanDo handles setup and provides UK-based support once you are trading.",
      },
    ],
    relatedProducts: ["ingenico-desk-5000", "ingenico-dx8000"],
    relatedServices: [
      { label: "EPOS Systems", href: "/epos-systems" },
      { label: "Switch & Save", href: "/switch-and-save" },
    ],
    relatedIndustries: [
      { label: "Hospitality", href: "/industries/hospitality" },
      { label: "Cafés & Coffee Shops", href: "/industries/cafes" },
    ],
    schemaType: "Product",
  },

  "ingenico-desk-5000": {
    slug: "ingenico-desk-5000",
    name: "Ingenico Desk 5000",
    shortName: "Desk 5000",
    manufacturer: "Ingenico",
    deviceType: "Countertop terminal",
    metaTitle: "Ingenico Desk 5000 Card Machine UK | Countertop Terminal",
    metaDescription:
      "The Ingenico Desk 5000 countertop card machine for UK shops, salons and service counters. Stable, secure fixed-till payments with CanDo support.",
    ogTitle: "Ingenico Desk 5000 Countertop Card Machine",
    ogDescription:
      "A stable, dependable countertop terminal for fixed checkouts, reception desks and service counters.",
    heroImage: `${CDN}/ingenico/desk-5000-side-angle-hero-right_colour_web_solid.png`,
    heroImageAlt: "Ingenico Desk 5000 countertop card machine",
    heroFramed: true,
    ogImage: `${CDN}/ingenico/desk-5000-side-angle-hero-right_colour_web_solid.png`,
    badges: ["Countertop", "Best Value", "Till Integration"],
    heroSubtitle:
      "A stable, dependable countertop card machine for fixed checkouts — straightforward payments at the till, reception or service counter.",
    quickAnswer: {
      heading: "Is the Desk 5000 a countertop terminal?",
      answer:
        "Yes — the Ingenico Desk 5000 is a countertop card machine designed for businesses taking secure payments from a fixed checkout, reception desk or service counter.",
      supporting:
        "It connects to your broadband or phone line for rock-solid stability, integrates easily with tills, and keeps the payment moment simple for staff and customers alike.",
    },
    atAGlance: [
      { label: "Best for", value: "Shops, salons & service counters" },
      { label: "Device type", value: "Countertop terminal" },
      { label: "Payment environment", value: "Fixed checkout or reception" },
      { label: "Connectivity", value: "Ethernet, broadband & phone line" },
      { label: "Mobility", value: "Fixed position" },
      { label: "Smart features", value: "Colour touchscreen" },
      { label: "Key strength", value: "Checkout stability & value" },
      { label: "Support model", value: "UK-based CanDo support" },
      { label: "CanDo fit", value: "Fixed-till businesses" },
    ],
    bestFor: [
      "Retail shops with a fixed till point",
      "Salons and clinics taking payment at reception",
      "Service counters and trade counters",
      "Offices taking in-person payments",
      "Businesses that want simple, wired reliability",
      "Anyone replacing an ageing countertop terminal",
    ],
    whyChoose: [
      {
        title: "Stability you can forget about",
        body: "A wired connection means no battery anxiety and no WiFi dropouts. The Desk 5000 sits at your counter and just works, transaction after transaction.",
      },
      {
        title: "Easy till integration",
        body: "The Desk 5000 integrates with till and EPOS setups, keeping totals accurate and end-of-day reconciliation quick.",
      },
      {
        title: "Best value for fixed checkouts",
        body: "If your payments all happen at one point, you don't pay for portability you won't use. The Desk 5000 is the value-smart choice for fixed tills.",
      },
    ],
    environment: {
      heading: "Where does the Desk 5000 fit best?",
      paragraphs: [
        "The Desk 5000 belongs wherever payments happen at one fixed point: the shop till, the salon reception, the clinic front desk, the trade counter. Its wired connection gives it the most stable payment experience of any terminal type.",
        "The hygienic PIN pad and clear colour screen keep transactions quick, and because it never leaves the counter it is always charged, always connected and always exactly where staff expect it.",
      ],
      image: `${CDN}/ingenico/desk-5000-side-angle-hero-left_colour_web_solid.png`,
      imageAlt: "Ingenico Desk 5000 countertop card machine side angle",
    },
    gallery: [
      { src: `${CDN}/ingenico/desk-5000-close-up-buttons_colour_web_solid.png`, alt: "Close up of the Ingenico Desk 5000 keypad" },
      { src: `${CDN}/ingenico/desk-5000-close-up-contactless-left_colour_web_solid.png`, alt: "Close up of the Ingenico Desk 5000 contactless reader" },
      { src: `${CDN}/ingenico/desk-5000-top-down-contents_colour_web_solid.png`, alt: "Ingenico Desk 5000 box contents from above" },
    ],
    capabilities: [
      "Contactless, chip & PIN and magstripe",
      "Apple Pay & Google Pay",
      "Till and EPOS integration",
      "Printed and digital receipts",
      "Hygienic, easy-clean PIN pad",
      "Optional cashback prompts",
    ],
    technicalHighlights: [
      "Wired Ethernet / broadband connection for maximum stability",
      "3.5\" colour touchscreen with physical keypad",
      "Fast thermal receipt printer",
      "Compact countertop footprint",
      "Mains powered — no charging required",
      "PCI-compliant, end-to-end encrypted payments",
    ],
    isRightFor: {
      yes: [
        "All your card payments happen at one fixed point",
        "You want the most stable, lowest-fuss terminal available",
        "You want easy till integration and quick end-of-day reconciliation",
      ],
      consider: [
        "You take payment at tables or on the floor — see the Move 5000",
        "You want a smart Android touchscreen experience — see the DX8000",
      ],
    },
    faqs: [
      {
        q: "What businesses is the Ingenico Desk 5000 best for?",
        a: "The Desk 5000 is best for shops, salons, clinics and service counters where payments happen at a fixed point. It is the value-smart choice when you don't need portability.",
      },
      {
        q: "How does the Desk 5000 connect to the internet?",
        a: "The Desk 5000 connects over wired Ethernet or your phone line. A wired connection makes it the most stable terminal type available — no WiFi dropouts, no battery to manage.",
      },
      {
        q: "Does the Desk 5000 work with till and EPOS systems?",
        a: "Yes. The Desk 5000 offers straightforward till and EPOS integration, keeping totals accurate and speeding up end-of-day reconciliation.",
      },
      {
        q: "Can the Desk 5000 take contactless and Apple Pay?",
        a: "Yes. The Desk 5000 accepts contactless payments, Apple Pay and Google Pay, plus chip & PIN and magstripe cards.",
      },
      {
        q: "Can I move the Desk 5000 around my premises?",
        a: "The Desk 5000 is designed to stay at a fixed point, connected to power and your network. If you need to take payments away from the counter, a portable terminal like the Ingenico Move 5000 is the better fit.",
      },
      {
        q: "Can CanDo help me replace my current countertop terminal?",
        a: "Yes. CanDo manages the switch end to end — statement review, rate comparison and setup — and UK-based support is included once you are live.",
      },
    ],
    relatedProducts: ["ingenico-move-5000", "elavon-mobile"],
    relatedServices: [
      { label: "EPOS Systems", href: "/epos-systems" },
      { label: "Switch & Save", href: "/switch-and-save" },
    ],
    relatedIndustries: [
      { label: "Retail", href: "/industries/retail" },
      { label: "Beauty & Salons", href: "/industries/beauty" },
    ],
    schemaType: "Product",
  },

  "verifone-t650p": {
    slug: "verifone-t650p",
    name: "Verifone T650P",
    shortName: "T650P",
    manufacturer: "Verifone",
    deviceType: "Portable Android terminal",
    metaTitle: "Verifone T650P Card Machine UK | Portable Android Terminal",
    metaDescription:
      "The Verifone T650P portable Android card machine for UK businesses. Sleek touchscreen payments and a premium customer experience with CanDo support.",
    ogTitle: "Verifone T650P Portable Android Card Machine",
    ogDescription:
      "A sleek, modern Android card machine for businesses that want premium portable payments and a customer-facing checkout experience.",
    heroImage: `${CDN}/verifone/t650c-front_colour_web_transparent.png`,
    heroImageAlt: "Verifone T650 series Android card machine",
    ogImage: `${CDN}/verifone/t650p_colour_web_solid.jpg`,
    badges: ["Android", "Sleek Design", "Customer-Facing"],
    heroSubtitle:
      "A sleek, modern Android card machine — premium portable payments with a customer-facing touchscreen experience your checkout deserves.",
    quickAnswer: {
      heading: "What is the Verifone T650P?",
      answer:
        "The Verifone T650P is a modern Android card machine for businesses wanting sleek portable payments, touchscreen usability and a premium customer-facing checkout experience.",
      supporting:
        "Lightweight and elegantly designed, the T650P focuses on the customer experience of paying — a bright full touchscreen, fast contactless and a device that looks at home in a design-conscious business.",
    },
    atAGlance: [
      { label: "Best for", value: "Modern retail & service businesses" },
      { label: "Device type", value: "Portable Android terminal" },
      { label: "Payment environment", value: "Counter and floor" },
      { label: "Connectivity", value: "WiFi, 4G & Bluetooth" },
      { label: "Mobility", value: "Lightweight & portable" },
      { label: "Smart features", value: "Android with full touchscreen" },
      { label: "Key strength", value: "Customer-facing experience" },
      { label: "Support model", value: "UK-based CanDo support" },
      { label: "CanDo fit", value: "Experience-led merchants" },
    ],
    bestFor: [
      "Modern retail with a design-conscious checkout",
      "Hospitality venues that want sleek portable payments",
      "Service-led businesses where the payment moment matters",
      "Customer-experience-focused merchants",
      "Businesses that want Android without enterprise complexity",
      "Teams that value light, easy-to-handle hardware",
    ],
    whyChoose: [
      {
        title: "Designed around your customer",
        body: "The T650P's bright, full touchscreen faces the customer with clear prompts — a payment experience that feels considered rather than transactional.",
      },
      {
        title: "Sleek and genuinely portable",
        body: "Slim and lightweight, the T650P moves easily between counter and floor without the bulk of a traditional portable terminal.",
      },
      {
        title: "Modern Android flexibility",
        body: "Android under the hood keeps the T650P current — modern payment types, smart receipts and room to grow, without enterprise-level complexity.",
      },
    ],
    environment: {
      heading: "Which businesses suit the Verifone T650P?",
      paragraphs: [
        "The T650P suits merchants who care how the payment moment feels. Boutiques, salons, studios and modern hospitality venues choose it for the same reason they care about their interior: it presents well and works beautifully.",
        "It differs from heavier smart terminals by prioritising lightness and customer-facing UX over back-office workflow tooling. If you want elegant portable payments rather than an operational command centre, the T650P hits the mark.",
      ],
      image: `${CDN}/verifone/t650c-right-angle_colour_web_transparent.png`,
      imageAlt: "Verifone T650 series terminal at an angle showing touchscreen",
    },
    gallery: [
      { src: `${CDN}/verifone/t650c-side_colour_web_transparent.png`, alt: "Verifone T650 series terminal side profile" },
      { src: `${CDN}/verifone/t650c-left-angle_colour_web_transparent.png`, alt: "Verifone T650 series terminal left angle" },
      { src: `${CDN}/verifone/t650c-back_colour_web_transparent.png`, alt: "Verifone T650 series terminal rear view" },
    ],
    capabilities: [
      "Contactless, chip & PIN and magstripe",
      "Apple Pay & Google Pay",
      "Customer-facing touchscreen prompts",
      "Digital and printed receipts",
      "EPOS integration options",
      "Mobile payments on the floor",
    ],
    technicalHighlights: [
      "Android operating system",
      "Full HD colour touchscreen",
      "WiFi, 4G and Bluetooth connectivity",
      "Slim, lightweight design",
      "All-day battery life",
      "PCI-compliant, end-to-end encrypted payments",
    ],
    isRightFor: {
      yes: [
        "The look and feel of your checkout matters to your brand",
        "You want modern Android payments without enterprise tooling",
        "You need light, portable hardware staff enjoy using",
      ],
      consider: [
        "You want advanced smart POS workflows and app ecosystems — see the DX8000",
        "You need a proven hospitality workhorse — see the Move 5000",
      ],
    },
    faqs: [
      {
        q: "What businesses is the Verifone T650P best for?",
        a: "The T650P is best for modern retail, hospitality and service-led businesses that want sleek portable payments and a premium customer-facing experience at checkout.",
      },
      {
        q: "Is the Verifone T650P an Android terminal?",
        a: "Yes. The T650P runs Android with a full touchscreen, giving it modern payment capabilities and smart receipt options in a lightweight portable design.",
      },
      {
        q: "How is the T650P different from the Ingenico DX8000?",
        a: "Both are Android terminals, but they focus differently. The T650P prioritises sleek portability and customer-facing UX; the DX8000 leans into advanced smart POS workflows and operational tooling. Experience-led merchants tend to pick the T650P, workflow-led operations the DX8000.",
      },
      {
        q: "Does the T650P take Apple Pay and Google Pay?",
        a: "Yes. The T650P accepts Apple Pay, Google Pay and all major contactless payments, plus chip & PIN and magstripe.",
      },
      {
        q: "Is the Verifone T650P portable?",
        a: "Yes. The T650P is slim, lightweight and connects over WiFi, 4G and Bluetooth, so it moves easily between the counter and the shop or venue floor.",
      },
      {
        q: "Can CanDo set up the T650P with my EPOS?",
        a: "Yes. The T650P offers EPOS integration options and CanDo can advise on the right pairing for your business as part of setup.",
      },
    ],
    relatedProducts: ["ingenico-dx8000", "ingenico-move-5000"],
    relatedServices: [
      { label: "EPOS Systems", href: "/epos-systems" },
      { label: "Switch & Save", href: "/switch-and-save" },
    ],
    relatedIndustries: [
      { label: "Retail", href: "/industries/retail" },
      { label: "Beauty & Salons", href: "/industries/beauty" },
    ],
    schemaType: "Product",
  },

  "elavon-mobile": {
    slug: "elavon-mobile",
    name: "Elavon Mobile",
    shortName: "Elavon Mobile",
    manufacturer: "Elavon",
    deviceType: "Mobile card reader",
    metaTitle: "Elavon Mobile Card Reader UK | Lightweight Mobile Payments",
    metaDescription:
      "Elavon Mobile is a lightweight card payment solution for UK mobile businesses, pop-ups and trades. Simple setup, low overhead, CanDo support.",
    ogTitle: "Elavon Mobile — Lightweight Mobile Card Payments",
    ogDescription:
      "A simple, flexible mobile card payment solution for businesses that need lightweight payment acceptance anywhere.",
    heroImage: `${CDN}/elavon/elavon-mobile-front-view-screen_colour_web_solid.webp`,
    heroImageAlt: "Elavon Mobile card reader",
    heroFramed: true,
    ogImage: `${CDN}/elavon/elavon-mobile_colour_web_transparent.png`,
    badges: ["Lightweight", "Low Overhead", "Simple Setup"],
    heroSubtitle:
      "Lightweight mobile payment acceptance for pop-ups, trades and businesses on the move — simple to set up, easy to carry, low overhead.",
    quickAnswer: {
      heading: "What is Elavon Mobile?",
      answer:
        "Elavon Mobile is a flexible mobile card payment solution for businesses that need lightweight payment acceptance away from a fixed checkout.",
      supporting:
        "Pair the compact reader with your phone and start taking card payments wherever you work — ideal for trades, mobile services, events and businesses that take payments occasionally rather than all day at a till.",
    },
    atAGlance: [
      { label: "Best for", value: "Mobile services, trades & pop-ups" },
      { label: "Device type", value: "Mobile card reader" },
      { label: "Payment environment", value: "Anywhere, via your phone" },
      { label: "Connectivity", value: "Bluetooth via smartphone" },
      { label: "Mobility", value: "Pocket-sized" },
      { label: "Smart features", value: "App-based payments & reporting" },
      { label: "Key strength", value: "Lightweight simplicity" },
      { label: "Support model", value: "UK-based CanDo support" },
      { label: "CanDo fit", value: "Micro & mobile businesses" },
    ],
    bestFor: [
      "Mobile services — trades, cleaners, therapists on the move",
      "Pop-ups, markets and event traders",
      "Microbusinesses starting to take card payments",
      "Occasional or seasonal payment takers",
      "Businesses that want minimal hardware and overhead",
      "A backup payment option for busy periods",
    ],
    whyChoose: [
      {
        title: "Genuinely lightweight",
        body: "A pocket-sized reader and your phone are the whole setup. No till, no wiring, no bulk — payments wherever the work takes you.",
      },
      {
        title: "Low overhead by design",
        body: "Elavon Mobile keeps costs lean, which makes card acceptance viable even for occasional and seasonal use.",
      },
      {
        title: "Simple from day one",
        body: "Pair the reader, open the app, take a payment. Setup is measured in minutes, not installation visits.",
      },
    ],
    environment: {
      heading: "How does Elavon Mobile compare with portable card machines?",
      paragraphs: [
        "A portable terminal like the Ingenico Move 5000 is built for all-day, high-volume service — it prints receipts, holds a charge through a full shift and lives in a busy venue. Elavon Mobile solves a different problem: making card acceptance effortless and affordable when payments happen on the move or now and then.",
        "If you take a handful of payments a day at changing locations — client visits, market stalls, events — Elavon Mobile gives you professional card acceptance without carrying or paying for hardware designed for a restaurant floor.",
      ],
      image: `${CDN}/elavon/elavon-mobile-side-angle-hero-right_colour_web_solid.webp`,
      imageAlt: "Elavon Mobile card reader side angle",
    },
    gallery: [
      { src: `${CDN}/elavon/elavon-mobile-close-up-screen_colour_web_solid.webp`, alt: "Close up of the Elavon Mobile screen" },
      { src: `${CDN}/elavon/elavon-mobile-close-up-card-port_colour_web_solid.png`, alt: "Close up of the Elavon Mobile card port" },
      { src: `${CDN}/elavon/elavon-mobile-top-down-contents_colour_web_solid.webp`, alt: "Elavon Mobile box contents from above" },
    ],
    capabilities: [
      "Contactless and chip & PIN",
      "Apple Pay & Google Pay",
      "Payments via your smartphone",
      "Digital receipts",
      "App-based sales reporting",
      "Works anywhere you have signal",
    ],
    technicalHighlights: [
      "Pocket-sized Bluetooth card reader",
      "Pairs with iOS and Android phones",
      "App-based setup and reporting",
      "Rechargeable battery",
      "No fixed installation required",
      "PCI-compliant, end-to-end encrypted payments",
    ],
    isRightFor: {
      yes: [
        "Your payments happen at changing locations or occasionally",
        "You want the lowest-overhead way to accept cards professionally",
        "You'd rather carry a pocket reader than a full terminal",
      ],
      consider: [
        "You take high volumes all day in one venue — see the Move 5000",
        "You want smart POS workflows on the device — see the DX8000",
      ],
    },
    faqs: [
      {
        q: "What businesses is Elavon Mobile best for?",
        a: "Elavon Mobile is best for mobile services, trades, pop-ups, market traders and microbusinesses that need lightweight, low-overhead card acceptance away from a fixed checkout.",
      },
      {
        q: "How does Elavon Mobile work?",
        a: "Elavon Mobile pairs a pocket-sized card reader with an app on your smartphone over Bluetooth. You take contactless and chip & PIN payments through the reader, and receipts and reporting live in the app.",
      },
      {
        q: "Does Elavon Mobile take Apple Pay and Google Pay?",
        a: "Yes. Elavon Mobile accepts Apple Pay, Google Pay and contactless cards, as well as chip & PIN payments.",
      },
      {
        q: "Is Elavon Mobile better than a portable card machine?",
        a: "It depends on volume. For all-day, high-volume service a portable terminal like the Ingenico Move 5000 is the better tool. For occasional payments at changing locations, Elavon Mobile is lighter, simpler and lower cost.",
      },
      {
        q: "How quickly can I start taking payments with Elavon Mobile?",
        a: "Setup takes minutes once your account is live — pair the reader with your phone, open the app and take your first payment. CanDo guides you through onboarding.",
      },
      {
        q: "Can I upgrade from Elavon Mobile to a full terminal later?",
        a: "Yes. Many CanDo customers start with Elavon Mobile and move to a portable or countertop terminal as volumes grow. We'll advise when an upgrade would pay for itself.",
      },
    ],
    relatedProducts: ["ingenico-move-5000", "paypal-reader"],
    relatedServices: [
      { label: "Virtual Terminal", href: "/virtual-terminal" },
      { label: "Online Payments", href: "/online-payments" },
    ],
    relatedIndustries: [
      { label: "Fitness & Training", href: "/industries/fitness" },
      { label: "Pet Services", href: "/industries/pet-services" },
    ],
    schemaType: "Product",
  },

  "paypal-reader": {
    slug: "paypal-reader",
    name: "PayPal Reader",
    shortName: "PayPal Reader",
    manufacturer: "PayPal",
    deviceType: "Mobile card reader (comparison)",
    metaTitle: "PayPal Reader Alternatives UK | Compare Card Readers",
    metaDescription:
      "Comparing the PayPal Reader for your UK business? See how reader-style devices stack up against dedicated card machines on support, settlement and cost.",
    ogTitle: "PayPal Reader — Is It Right for Your Business?",
    ogDescription:
      "An honest comparison guide: when a PayPal-style card reader fits, and when a dedicated card machine serves a growing business better.",
    heroImage: `${CDN}/zettle/zettle_colour_web_transparent.png`,
    heroImageAlt: "Zettle by PayPal card reader",
    ogImage: null,
    badges: ["Comparison Guide", "Buyer's Guide"],
    heroSubtitle:
      "Weighing up a PayPal card reader? Here's an honest look at where reader-style devices fit — and what growing businesses compare before choosing.",
    quickAnswer: {
      heading: "Is a PayPal Reader right for your business?",
      answer:
        "PayPal Reader-style card readers can suit startups and small businesses taking occasional payments, but growing businesses often compare support, reporting, settlement and long-term costs before choosing a payment setup.",
      supporting:
        "This page is a buyer's guide, not a sales page. We supply Zettle by PayPal alongside dedicated terminals, so we'll give you the honest trade-offs — and help you pick whichever genuinely fits.",
    },
    atAGlance: [
      { label: "Best for", value: "Startups & occasional payments" },
      { label: "Device type", value: "App-paired card reader" },
      { label: "Payment environment", value: "Anywhere, via your phone" },
      { label: "Connectivity", value: "Bluetooth via smartphone" },
      { label: "Mobility", value: "Pocket-sized" },
      { label: "Smart features", value: "App-based sales tools" },
      { label: "Key strength", value: "Fast, familiar setup" },
      { label: "Support model", value: "Self-serve (provider-dependent)" },
      { label: "CanDo fit", value: "We help you compare options" },
    ],
    bestFor: [
      "Startups taking their first card payments",
      "Microbusinesses with low, irregular volumes",
      "Side projects and seasonal sellers",
      "Businesses already selling through PayPal online",
      "Anyone comparing readers before committing to a terminal",
      "Merchants reviewing their payment options as they grow",
    ],
    whyChoose: [
      {
        title: "Where reader-style devices shine",
        body: "For occasional payments, a reader paired with your phone is quick to set up, familiar to customers and cheap to start with. For many startups it is a sensible first step.",
      },
      {
        title: "What growing businesses compare",
        body: "As volumes rise, merchants tend to weigh per-transaction costs against monthly pricing, and self-serve help against a named support team. Settlement speed and reporting depth start to matter more too.",
      },
      {
        title: "How CanDo helps",
        body: "We supply Zettle by PayPal alongside dedicated terminals, so we're not here to talk you out of a reader — we'll compare your real numbers and recommend whichever setup genuinely costs you less and supports you better.",
      },
    ],
    environment: {
      heading: "Reader or card machine — how do you decide?",
      paragraphs: [
        "The honest answer is volume and dependence. If card payments are occasional and your business wouldn't stop if the reader did, a PayPal-style reader is a perfectly good tool. If card payments are how you get paid every day, the calculus changes: per-transaction pricing compounds, and waiting on self-serve support during a busy Saturday costs real money.",
        "Growing businesses also start to care about settlement — how quickly money lands and whether it arrives gross or net — and about reporting that reconciles cleanly. These are the areas where dedicated card machines with a managed service tend to pull ahead.",
      ],
      image: `${CDN}/zettle/reader-front-with-card_colour_web_transparent.png`,
      imageAlt: "Zettle by PayPal reader with card inserted",
    },
    gallery: [],
    capabilities: [
      "Contactless and chip & PIN",
      "Apple Pay & Google Pay",
      "Payments via a paired smartphone",
      "Digital receipts",
      "App-based sales tools",
      "Familiar PayPal brand at checkout",
    ],
    technicalHighlights: [
      "Pocket-sized Bluetooth reader",
      "Pairs with iOS and Android apps",
      "Per-transaction pricing model",
      "Self-serve onboarding",
      "App-store style feature updates",
      "PCI-compliant payments",
    ],
    isRightFor: {
      yes: [
        "You're starting out and want the fastest route to accepting cards",
        "Your card volumes are low or seasonal",
        "You value familiar branding over managed support",
      ],
      consider: [
        "Card payments are your daily lifeline — compare the Move 5000 or DX8000",
        "You want named UK support and negotiated rates — talk to CanDo",
      ],
    },
    faqs: [
      {
        q: "Is a PayPal Reader good for small businesses?",
        a: "For startups and businesses taking occasional payments, a PayPal-style reader is a quick, low-commitment way to accept cards. Growing businesses usually compare per-transaction costs, support and settlement before deciding whether to stay on a reader or move to a dedicated terminal.",
      },
      {
        q: "What are the alternatives to a PayPal Reader in the UK?",
        a: "Alternatives include lightweight readers like Elavon Mobile and dedicated terminals like the Ingenico Move 5000 or DX8000 with a managed merchant account. The right choice depends on your volumes, environment and how much support you want.",
      },
      {
        q: "When does a dedicated card machine beat a card reader?",
        a: "Typically when card payments become daily and business-critical. Dedicated terminals offer negotiated rates, named UK support, faster and cleaner settlement, and hardware built for all-day use.",
      },
      {
        q: "Does CanDo supply PayPal card readers?",
        a: "CanDo supplies Zettle by PayPal alongside dedicated card machines, so we can set you up with either — and give you an honest comparison based on your real transaction volumes first.",
      },
      {
        q: "Can CanDo compare my current reader costs with a card machine?",
        a: "Yes. Send us a month of sales figures or a statement and we'll show you side by side what a reader and a dedicated terminal would cost, including settlement and support differences. No obligation.",
      },
    ],
    relatedProducts: ["elavon-mobile", "ingenico-move-5000"],
    relatedServices: [
      { label: "Switch & Save", href: "/switch-and-save" },
      { label: "Statement Review", href: "/statement-review" },
    ],
    relatedIndustries: [
      { label: "E-Commerce", href: "/industries/e-commerce" },
      { label: "Retail", href: "/industries/retail" },
    ],
    schemaType: "Page",
  },
};

/** Ordered list for hub cards, comparison tables and related-product lookups. */
export const CARD_MACHINE_PRODUCT_LIST = Object.values(CARD_MACHINE_PRODUCTS);

/**
 * Comparison matrix rows — one per product, referenced by every product page.
 * Kept separate from the per-product objects so the table reads consistently
 * and wording stays differentiated (no copy reuse between pages).
 */
export const COMPARISON_ROWS = [
  {
    slug: "pax-a920-pro",
    product: "PAX A920 Pro",
    bestFor: "All-round flexibility",
    portable: true,
    countertop: true,
    android: true,
    connectivity: "WiFi · 4G",
    smartFeatures: "Business apps & staff controls",
    recommendedFor: "Growing multi-tasking businesses",
  },
  {
    slug: "ingenico-dx8000",
    product: "Ingenico DX8000",
    bestFor: "Smart POS workflows",
    portable: true,
    countertop: true,
    android: true,
    connectivity: "WiFi · 4G · Bluetooth",
    smartFeatures: "Android apps & smart POS",
    recommendedFor: "Hospitality & advanced retail",
  },
  {
    slug: "ingenico-move-5000",
    product: "Ingenico Move 5000",
    bestFor: "Table service & queue busting",
    portable: true,
    countertop: false,
    android: false,
    connectivity: "WiFi · 4G · Bluetooth",
    smartFeatures: "Colour touchscreen",
    recommendedFor: "Restaurants, cafés & bars",
  },
  {
    slug: "ingenico-desk-5000",
    product: "Ingenico Desk 5000",
    bestFor: "Fixed checkout stability",
    portable: false,
    countertop: true,
    android: false,
    connectivity: "Ethernet · phone line",
    smartFeatures: "Colour touchscreen",
    recommendedFor: "Shops, salons & counters",
  },
  {
    slug: "verifone-t650p",
    product: "Verifone T650P",
    bestFor: "Customer-facing experience",
    portable: true,
    countertop: true,
    android: true,
    connectivity: "WiFi · 4G · Bluetooth",
    smartFeatures: "Full touchscreen UX",
    recommendedFor: "Modern retail & services",
  },
  {
    slug: "elavon-mobile",
    product: "Elavon Mobile",
    bestFor: "Lightweight mobile payments",
    portable: true,
    countertop: false,
    android: false,
    connectivity: "Bluetooth via phone",
    smartFeatures: "App-based payments",
    recommendedFor: "Trades, pop-ups & mobile services",
  },
  {
    slug: "paypal-reader",
    product: "PayPal Reader",
    bestFor: "Occasional payments",
    portable: true,
    countertop: false,
    android: false,
    connectivity: "Bluetooth via phone",
    smartFeatures: "App-based sales tools",
    recommendedFor: "Startups & microbusinesses",
  },
];
